import Cookies from 'js-cookie';
import axios from 'axios';
import qs from 'qs';
import { getEnvConfig, useEnv } from '../utils/env';
import { cgiModeMap } from '@/service/const';

// 太湖登录相关常量
export const TAIHU_LOGIN_TYPE = 'taihu';
export const TAIHU_JWT_COOKIE_NAME = 'imarketing-jwt-sso';
export const TAIHU_LOGIN_TYPE_COOKIE_NAME = 'imarketing-login-type';
export const TAIHU_USERNAME_COOKIE_NAME = 'imarketing-username';
const GLOBAL_URL_PREFIX = 'api';
const taihuClientId = 'imarketing';
const taihuCallbackPath = '/api/auth/taihu/login-callback';
const taihuBase = 'https://tai.it.tencent.com/api/auth-center/oauth2';


// Cookie配置
export const cookieOptions = {
  get domain() {
    return getEnvConfig().cookieDomain;
  },
  expires: 1, // 1天
  get secure() {
    return getEnvConfig().secure;
  },
  sameSite: 'lax' as const,
};

/**
 * 创建用于太湖认证的axios实例
 */
function createAuthAxios() {
  const { apiBaseUrl } = getEnvConfig();
  
  return axios.create({
    baseURL: apiBaseUrl,
    timeout: 10000,
    withCredentials: true
  });
}

/**
 * 发起太湖登录
 * @param redirectUrl 登录成功后的重定向地址
 */
export function loginByTaihu(redirectUrl?: string) {
  let envConfig = getEnvConfig();
  let baseUrl = envConfig.apiBaseUrl;
  if (envConfig.env === 'dev') {
    baseUrl = `https://${cgiModeMap.test}`;
  }
  const callbackUrl = `${baseUrl}${taihuCallbackPath}`;
  window.location.href = `${taihuBase}/authorize?${qs.stringify({
    response_type: 'code',
    client_id: taihuClientId,
    scope: 'openid offline',
    redirect_uri: callbackUrl,
    state: `${redirectUrl}|-|${callbackUrl}`,
  })}`;
}

/**
 * 直接跳转到太湖登录页面
 * @param currentPath 当前页面路径，用于登录成功后跳转回来
 */
export function redirectToTaihuLogin() {
  const redirectUrl = location.href;
  loginByTaihu(redirectUrl);
}

/**
 * 获取太湖JWT Token
 */
export function getTaihuToken(): string | undefined {
  return Cookies.get(TAIHU_JWT_COOKIE_NAME);
}

/**
 * 检查太湖登录状态
 */
export function checkTaihuLogin(): boolean {
  const token = Cookies.get(TAIHU_JWT_COOKIE_NAME);
  return !!(token);
}

/**
 * 获取太湖登录用户名
 */
export function getTaihuUsername(): string | undefined {
  return Cookies.get(TAIHU_USERNAME_COOKIE_NAME);
}

/**
 * 验证太湖Token
 * @param token JWT Token (可选，如果不提供则使用cookie中的token)
 */
export async function verifyTaihuToken(token?: string): Promise<{ valid: boolean; [key: string]: any }> {
  try {
    const tokenToVerify = token || getTaihuToken();
    if (!tokenToVerify) {
      return { valid: false, message: '未找到有效的Token' };
    }
    
    const axiosInstance = createAuthAxios();
    const response = await axiosInstance.post(`/${GLOBAL_URL_PREFIX}/auth/taihu/verify`, { token: tokenToVerify });
    return response.data;
  } catch (error) {
    console.error('验证太湖Token失败:', error);
    return { valid: false };
  }
}

/**
 * 获取太湖用户信息
 */
export async function getTaihuUserInfo(): Promise<any> {
  const token = getTaihuToken();
  if (!token) {
    throw new Error('未找到太湖Token');
  }

  try {
    const axiosInstance = createAuthAxios();
    const response = await axiosInstance.get(`/${GLOBAL_URL_PREFIX}/auth/taihu/userinfo?token=${token}`);
    return response.data;
  } catch (error) {
    console.error('获取太湖用户信息失败:', error);
    throw error;
  }
}

/**
 * 太湖登出
 */
export async function logoutTaihu(): Promise<void> {
  try {
    const axiosInstance = createAuthAxios();
    const response = await axiosInstance.post(`/${GLOBAL_URL_PREFIX}/auth/taihu/logout`);
    
    // 清除本地cookie
    Cookies.remove(TAIHU_JWT_COOKIE_NAME, cookieOptions);
    Cookies.remove(TAIHU_LOGIN_TYPE_COOKIE_NAME, cookieOptions);
    Cookies.remove(TAIHU_USERNAME_COOKIE_NAME, cookieOptions);
    
    // 如果有太湖登出URL，跳转到太湖登出页面
    if (response.data.logout_url) {
      window.location.href = response.data.logout_url;
    } else {
      // 否则刷新页面
      window.location.reload();
    }
  } catch (error) {
    console.error('太湖登出失败:', error);
    // 即使登出失败，也清除本地cookie
    Cookies.remove(TAIHU_JWT_COOKIE_NAME, cookieOptions);
    Cookies.remove(TAIHU_LOGIN_TYPE_COOKIE_NAME, cookieOptions);
    Cookies.remove(TAIHU_USERNAME_COOKIE_NAME, cookieOptions);
    window.location.reload();
  }
}

/**
 * 统一登录入口
 * @param redirectUrl 登录成功后的重定向地址
 */
export function login(redirectUrl?: string) {
  // 这里可以根据配置决定使用哪种登录方式
  // 目前默认使用太湖登录
  loginByTaihu(redirectUrl);
}

/**
 * 统一登出入口
 */
export async function logout() {
  if (isTaihuLogin()) {
    await logoutTaihu();
  } else {
    // 处理其他登录方式的登出
    window.location.reload();
  }
}

/**
 * 检查登录状态
 */
export function checkLogin(): boolean {
  return checkTaihuLogin();
}

/**
 * 获取当前用户名
 */
export function getCurrentUsername(): string | undefined {
  return getTaihuUsername();
}